import { analyzeScan, SCAN_DATE_FILLS, SCAN_FILL_NAMES, type ScanFillName, type ScanResult } from '@ayumia/component-runtime';
import type { ListInput } from '../../api/client';

/**
 * Règles du scan (widgets `scan_code` et `lot`, ADR-146 addendum (6)), mêmes que la tablette
 * (`apps/operator/lib/features/task/application/gs1_scan.dart`) : lecture par l'analyseur GS1 unique
 * du Core, champs remplis, lecture affichée en clair, requêtes des lots proposés. Présentation : le
 * serveur relit le code à la complétion et refuse un écart.
 */

/** Fin de rafale d'une douchette (`presentation.scan.suffix`, Entrée par défaut). */
export type ScanSuffix = 'enter' | 'tab';

/** Touche du clavier qui termine la rafale. */
export function suffixKey(suffix: ScanSuffix | undefined): 'Enter' | 'Tab' {
  return suffix === 'tab' ? 'Tab' : 'Enter';
}

/** Séparateur GS1 (FNC1 dans les données) tel qu'une douchette le tape : U+001D, ou Ctrl+]. */
export function isGroupSeparatorKey(key: string, ctrl: boolean): boolean {
  return key === '\u001d' || (ctrl && key === ']');
}

/** Année de référence du siècle d'une date GS1 : celle du jour de la fiche, sinon de l'horloge. */
export function referenceYearOf(today: string | null | undefined, now: () => Date = () => new Date()): number {
  return typeof today === 'string' && /^\d{4}-/.test(today) ? Number(today.slice(0, 4)) : now().getUTCFullYear();
}

export function readScan(raw: string, today: string | null | undefined): ScanResult {
  return analyzeScan(raw, { referenceYear: referenceYearOf(today) });
}

/** Réponses des champs remplis par une lecture : `{ champ: valeur }`, seulement pour les valeurs que le code porte. */
export function scanFillValues(result: ScanResult, fills: Readonly<Record<string, string>> | undefined): Record<string, string> {
  if (!result.ok || fills === undefined) return {};
  const out: Record<string, string> = {};
  for (const name of SCAN_FILL_NAMES) {
    const target = fills[name];
    const value = result.values[name];
    if (target !== undefined && value !== undefined) out[target] = value;
  }
  return out;
}

export const SCAN_LABELS: Readonly<Record<ScanFillName, string>> = {
  gtin: 'Code article',
  lot: 'Lot',
  serial: 'N° de série',
  sscc: 'N° de colis',
  expiry: 'À consommer jusqu’au',
  best_before: 'À consommer de préférence avant',
  production: 'Fabriqué le',
};

/** Date civile AAAA-MM-JJ montrée JJ/MM/AAAA. */
export function civilShown(date: string): string {
  return `${date.slice(8, 10)}/${date.slice(5, 7)}/${date.slice(0, 4)}`;
}

/** Lignes de la lecture affichée : libellé en clair et valeur (date JJ/MM/AAAA), dans l'ordre des valeurs nommées. */
export function scanReadout(result: ScanResult): Array<{ name: ScanFillName; label: string; value: string }> {
  if (!result.ok) return [];
  return SCAN_FILL_NAMES.filter((name) => result.values[name] !== undefined).map((name) => ({ name, label: SCAN_LABELS[name], value: SCAN_DATE_FILLS.has(name) ? civilShown(result.values[name]!) : result.values[name]! }));
}

/** Lots lus pour les propositions, au plus. */
export const LOT_LIMIT = 50;

/** Liste des lots actifs de la tâche, recherche serveur sur le numéro (ADR-141 addendum (2), ADR-094). */
export function lotQuery(search: string, workItemId: string | undefined): ListInput {
  const q = search.trim().slice(0, 100);
  return { filter: { status: { eq: 'active' } }, limit: LOT_LIMIT, ...(q === '' ? {} : { q }), ...(workItemId === undefined || workItemId === '' ? {} : { work_item_id: workItemId }) };
}

/** Soldes disponibles de ces lots, dans le même périmètre. */
export function balanceQuery(lotIds: readonly string[], workItemId: string | undefined): ListInput {
  return { filter: { lot_id: { in: lotIds.join(',') }, stock_state: { eq: 'available' } }, limit: 200, ...(workItemId === undefined || workItemId === '' ? {} : { work_item_id: workItemId }) };
}
